import type { StaffCatalogHistory } from "@lafayette/schemas";
import { useInfiniteQuery } from "@tanstack/react-query";

import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { SectionCard } from "../../ui/section-card";
import { hintText, listRow, plainList } from "../components/styles";
import { describeCatalogError } from "../errors";
import { formatDateTime, historyActionLabel, humanizeKey } from "../labels";
import { useCatalogRuntime } from "../runtime";

const PAGE_SIZE = 20;

type HistoryEvent = StaffCatalogHistory["events"][number];

/** A metadata value is a code, a count or a flag (never text a person typed). */
function metadataValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (value === null || value === undefined) return "none";
  return String(value);
}

function describeActor(event: HistoryEvent): string {
  if (event.actorName !== null) return event.actorName;
  return event.actorKind === "staff" ? "A staff member" : event.actorKind;
}

/**
 * Who changed the item, when, and what kind of change. There is no before-and-after text: the audit
 * record holds codes, counts and flags only. The list reloads when the item's version moves, so a
 * change made on this page shows up at once.
 */
export function HistoryPanel({ itemId, version }: Readonly<{ itemId: string; version: number }>) {
  const { api } = useCatalogRuntime();
  const query = useInfiniteQuery({
    queryKey: ["catalog", "history", itemId, version],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.listHistory(
        itemId,
        { limit: PAGE_SIZE, ...(pageParam === null ? {} : { cursor: pageParam }) },
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const events = query.data?.pages.flatMap((page) => page.events) ?? [];

  return (
    <SectionCard
      id="history"
      title="History"
      description="Who changed this item and when. It shows the kind of change, not the text."
    >
      {query.isPending ? (
        <AsyncState kind="loading" title="Loading history" message="Reading the change history." />
      ) : query.isError ? (
        <AsyncState
          kind="error"
          title="History could not be loaded"
          message={describeCatalogError(query.error).message}
          onRetry={() => void query.refetch()}
        />
      ) : events.length === 0 ? (
        <p className={hintText}>No history yet.</p>
      ) : (
        <ol className={plainList}>
          {events.map((event) => (
            <li key={event.sequence} className={cn(listRow, "grid gap-1")}>
              <p className="m-0">
                <strong>{historyActionLabel(event.action)}</strong>
              </p>
              <p className={hintText}>
                {describeActor(event)} · {formatDateTime(event.occurredAt)}
              </p>
              {event.reason !== null ? <p className="m-0">Reason: {event.reason}</p> : null}
              {Object.keys(event.metadata).length > 0 ? (
                <dl className="m-0 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {Object.entries(event.metadata).map(([key, value]) => (
                    <div key={key} className="flex gap-1">
                      <dt className="text-muted-foreground">{humanizeKey(key)}</dt>
                      <dd className="m-0">{metadataValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {query.hasNextPage ? (
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? "Loading…" : "Load more history"}
          </Button>
        </div>
      ) : null}
    </SectionCard>
  );
}
